import { EngineStore } from '~/stores/engine'
import { SessionStore } from '~/stores/session'
import { QuoteUtils, valuesForHumans } from '~/composables/QuoteUtils'
import { PlanType, CsnpQualifierType } from '~/generated/api-clients-generated'
import { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
import { PharmacyCoverageLevel } from '~/models/quoting/PharmacyCoverage'
import { now } from '~/utils/dates'
import { ProfileStore } from '~/stores/profile'
import QuotingConfig from '~/composables/QuotingConfig'

export class QuoteAnalytics {
  static use() {
    const { track } = Analytics.use()

    const _debouncedTrack = _debounce(track, 1000)

    function quotesLoaded() {
      const engine = EngineStore.use()
      const session = SessionStore.use()
      const carriers = CarrierStore.use()
      const runtimeConfig = useRuntimeConfig()
      const { quotingConfig } = QuotingConfig.use()

      const bestPlan = engine.displayedQuotes[0]

      const logoUrlForPlan = (logoKey: string, year: number) =>
        `${runtimeConfig.public.carrierAssetsBaseUrl}/${carriers.getLogo(logoKey, year)}`

      const bestPlanDetails = {
        name: bestPlan.name,
        carrierLogo: logoUrlForPlan(bestPlan.details.logoKey!, bestPlan.details.year!),
        brandName: bestPlan.details.brandName,
        premium: bestPlan.details.premium,
        score: bestPlan.score
      }

      const { selection: _, ...whatBringsYouInContext } = session.whatBringsYouIn ?? {}
      const [contextKey, contextValue] = Object.entries(whatBringsYouInContext)[0] ?? []

      const allLogos = _uniqBy(engine.displayedQuotes, (x) => x.details.logoKey).map((x) =>
        logoUrlForPlan(x.details.logoKey!, x.details.year!)
      )

      _debouncedTrack('QuotesLoaded', {
        ...getEngineEventProps(),
        ...getFootprintEventProps(),
        orderedPlanIds: engine.displayedQuotes.map((x) => x.medicareId),
        footprintLogos: allLogos,
        bestPlan: bestPlanDetails,
        whatBringsYouIn: session.whatBringsYouIn?.selection ?? null,
        whatBringsYouInContext: contextKey ? `${contextKey}:${contextValue}` : null,
        ghostCard: quotingConfig.value.ghostCard ?? false
      })
    }

    function quotePageVariation(variation: string) {
      track('QuotePageVariation', {
        ...getEngineEventProps(),
        variation
      })
    }

    function planFinderEngineParams() {
      _debouncedTrack('PlanFinderEngineParams', {
        ...getEngineEventProps()
      })
    }

    function planTypeSelected(planType: PlanType) {
      const session = SessionStore.use()

      _debouncedTrack('PlanTypeSelected', {
        profileId: session.profileId,
        planType: PlanType[planType]
      })
    }

    function getFootprintEventProps() {
      const session = SessionStore.use()

      return {
        totalCarriers: _uniq(session.carrierFootprint?.map((x) => x.carrierKey) ?? [])?.length,
        totalPlans:
          session.carrierFootprint
            ?.flatMap((carrier) => carrier.planTypes)
            ?.reduce((prev, cur) => prev + (cur?.count ?? 0), 0) ?? 0
      }
    }

    function getEngineEventProps() {
      const engine = EngineStore.use()
      const session = SessionStore.use()
      const profile = ProfileStore.use()
      const { defaultSort } = QuoteUtils.useSort()

      return {
        profileId: session.profileId,
        email: profile.emailAddress?.address,
        mobilePhone: profile.primaryContactPhone?.number,
        engineId: engine.id,
        timestamp: now().format(),
        zip: engine.params.zip,
        fips: engine.params.county,
        state: engine.params.state,
        planYear: engine.params.planYear,
        planType: PlanType[engine.selectedPlanType!],
        filters: valuesForHumans(session.quoteFilters),
        sort: session.quoteSortOrder === 'default' ? defaultSort.value : session.quoteSortOrder,
        compare: session.comparedPlans,
        rxs: engine.params?.rxs?.map((x) => x.uuid) ?? [],
        doctors: engine.params?.doctors?.map((x) => x.uuid) ?? [],
        pharmacy: engine.params?.pharmacy?.uuid ?? null,
        benefits: engine.params?.additionalBenefits?.map((x) => x),
        planList: session.planList,
        csnpQualifiers: profile.csnpQualifiers?.map((c) => CsnpQualifierType[c]?.toString()) ?? [],
        dsnpEligible: profile.medicaidInfo?.hasMedicaid ?? false,
        tagCatalog: engine.displayedQuotes?.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.medicareId]: curr.planAttributes
          }),
          {}
        )
      }
    }

    function getQuotePageEventProps(elementKey: string) {
      return {
        key: elementKey,
        ...getEngineEventProps()
      }
    }

    function getQuoteCardEventProps(elementKey: string, quote: QuotedPlanVm) {
      const engine = EngineStore.use()

      return {
        key: elementKey,
        ...getEngineEventProps(),
        medicareId: quote.medicareId,
        score: quote.score,
        rank: engine.displayedQuotes.findIndex((x) => x.medicareId === quote.medicareId),
        rxsCovered: quote.drugCoverage.coverageSummaries
          .filter((x) => x.isCovered)
          .map((x) => x.uuid),
        doctorsCovered: quote.providerCoverage.providers
          .filter((x) => x.inNetwork)
          .map((x) => x.uuid),
        pharmacyStatus:
          quote.pharmacyCoverage.pharmacies.length > 0
            ? PharmacyCoverageLevel[quote.pharmacyCoverage.coverageLevel]
            : null,
        benefits: quote.additionalBenefits.additionalBenefits
          .filter((x) => x.covered)
          .map((x) => x.name)
      }
    }

    return {
      quotesLoaded,
      quotePageVariation,
      planTypeSelected,
      getQuoteCardEventProps,
      getQuotePageEventProps,
      getEngineEventProps
    }
  }
}
